<template>
	<v-dialog v-model="dialog" max-width="1100" persistent scrollable>
		<v-card>
			<v-card-title class="border_bottom">
				Carica modifiche catalogo {{ platformLabel }}
				<v-spacer></v-spacer>
				<v-btn icon @click="close">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text class="pt-4">
				<!-- passo 1: scelta file e marketplace -->
				<template v-if="step === 1">
					<v-select v-model="marketplace" :items="marketplaceOptions" :loading="loadingMarketplaces" label="Marketplace" item-text="text" item-value="value" outlined dense hide-details></v-select>

					<v-file-input v-model="file" label="File modificato (.xlsx o .csv)" accept=".xlsx,.xls,.csv" prepend-icon="mdi-file-excel" outlined dense show-size hide-details class="mt-4"></v-file-input>

					<v-alert type="info" text dense class="mt-4 mb-0">
						Compila solo le colonne <strong>NuovoPrezzo</strong>, <strong>NuovaQt</strong> e <strong>NuovoStatus</strong>: le righe lasciate vuote non vengono toccate. Prima di applicare vedrai
						l'elenco dei prodotti che verranno modificati e potrai togliere quelli che non vuoi applicare.
					</v-alert>

					<v-alert type="warning" text dense class="mt-3 mb-0">
						<div>La colonna <strong>NuovoStatus</strong> permette solo di <strong>chiudere</strong> una pubblicazione: per riaprirla va ripubblicato il prodotto.</div>
						<div class="mt-1">La <strong>giacenza</strong> forzata dal file vale fino al successivo ricalcolo dal magazzino.</div>
					</v-alert>
				</template>

				<!-- passo 2: anteprima di quello che verrà modificato -->
				<template v-else>
					<div class="d-flex flex-wrap align-center mb-3">
						<v-chip class="mr-2 mb-2" color="primary" outlined>{{ counters.changed || 0 }} prodotti da modificare</v-chip>
						<v-chip class="mr-2 mb-2" outlined>{{ counters.price || 0 }} prezzi</v-chip>
						<v-chip class="mr-2 mb-2" outlined>{{ counters.quantity || 0 }} giacenze</v-chip>
						<v-chip class="mr-2 mb-2" outlined>{{ counters.status || 0 }} chiusure</v-chip>
						<v-chip class="mr-2 mb-2" outlined>{{ counters.unchanged || 0 }} invariate</v-chip>
						<v-chip v-if="counters.invalid" class="mr-2 mb-2" color="error" outlined>{{ counters.invalid }} scartate</v-chip>
						<v-chip v-if="excludedCount" class="mr-2 mb-2" color="warning" outlined close @click:close="includeAll">{{ excludedCount }} escluse da te</v-chip>
					</div>

					<div v-if="tab === 0" class="caption grey--text text--darken-1 mb-2">
						Togli la spunta alle righe che non vuoi applicare: tutte le altre partono insieme quando premi "Applica".
					</div>

					<v-tabs v-model="tab" class="mb-2">
						<v-tab>Da modificare</v-tab>
						<v-tab v-if="counters.invalid">Righe scartate</v-tab>
					</v-tabs>

					<v-data-table
						v-if="tab === 0"
						:headers="headers"
						:items="rows"
						:loading="loadingRows"
						:server-items-length="total"
						:options.sync="options"
						:footer-props="{ 'items-per-page-options': [25, 50, 100] }"
						:item-class="rowClass"
						dense
						class="elevation-0"
						:hide-default-header="mobileCards"
						:class="{ 'table-cards': mobileCards }"
					>
						<!-- spunta di tutta la pagina visibile -->
						<template v-slot:header.include>
							<v-simple-checkbox :value="pageIncluded" :indeterminate="pageIndeterminate" color="primary" @input="togglePage"></v-simple-checkbox>
						</template>
						<template v-slot:item.include="{ item }">
							<v-simple-checkbox :value="isIncluded(item)" color="primary" @input="toggleRow(item)"></v-simple-checkbox>
						</template>
						<!-- telefono: ogni riga diventa una card chiave/valore -->
						<template v-if="mobileCards" v-slot:body="{ items }">
							<tbody class="catalog-cards">
								<tr v-if="items.length === 0">
									<td :colspan="headers.length" class="px-3 py-1">
										<div class="grey--text text-center py-4">Nessun elemento</div>
									</td>
								</tr>
								<tr v-for="item in items" :key="'riga' + item.line">
									<td :colspan="headers.length" class="px-3 py-1">
										<key-value-card :title="item.sellerSku" :fields="campiCard(item)" :class="{ 'row-excluded': !isIncluded(item) }">
											<template v-slot:actions>
												<v-simple-checkbox :value="isIncluded(item)" color="primary" class="mr-2" @input="toggleRow(item)"></v-simple-checkbox>
											</template>
										</key-value-card>
									</td>
								</tr>
							</tbody>
						</template>
						<template v-slot:item.price="{ item }">
							<span v-if="item.next.price !== undefined"
								>{{ item.current.price }} <v-icon small>mdi-arrow-right</v-icon> <strong>{{ item.next.price }}</strong></span
							>
							<span v-else class="grey--text">—</span>
						</template>
						<template v-slot:item.quantity="{ item }">
							<span v-if="item.next.quantity !== undefined"
								>{{ item.current.quantity }} <v-icon small>mdi-arrow-right</v-icon> <strong>{{ item.next.quantity }}</strong></span
							>
							<span v-else class="grey--text">—</span>
						</template>
						<template v-slot:item.status="{ item }">
							<span v-if="item.next.status !== undefined"
								>{{ item.current.status }} <v-icon small>mdi-arrow-right</v-icon> <strong>{{ item.next.status }}</strong></span
							>
							<span v-else class="grey--text">—</span>
						</template>
					</v-data-table>

					<v-data-table
						v-else
						:headers="invalidHeaders"
						:items="rows"
						:loading="loadingRows"
						:server-items-length="total"
						:options.sync="options"
						:footer-props="{ 'items-per-page-options': [25, 50, 100] }"
						dense
						class="elevation-0"
						:hide-default-header="mobileCards"
						:class="{ 'table-cards': mobileCards }"
					>
						<!-- telefono: ogni riga scartata diventa una card chiave/valore -->
						<template v-if="mobileCards" v-slot:body="{ items }">
							<tbody class="catalog-cards">
								<tr v-if="items.length === 0">
									<td :colspan="invalidHeaders.length" class="px-3 py-1">
										<div class="grey--text text-center py-4">Nessun elemento</div>
									</td>
								</tr>
								<tr v-for="item in items" :key="'scartata' + item.line">
									<td :colspan="invalidHeaders.length" class="px-3 py-1">
										<key-value-card
											:title="item.sellerSku"
											:fields="[
												{ label: 'Riga', value: item.line },
												{ label: 'Motivo dello scarto', value: item.error, strong: true },
											]"
										></key-value-card>
									</td>
								</tr>
							</tbody>
						</template>
					</v-data-table>
				</template>
			</v-card-text>

			<v-card-actions class="border_top">
				<v-btn v-if="step === 2" text @click="back">Indietro</v-btn>
				<v-spacer></v-spacer>
				<v-btn text @click="close">Annulla</v-btn>
				<v-btn v-if="step === 1" color="primary" :disabled="!file || !marketplace" :loading="loadingPreview" @click="loadPreview">Anteprima</v-btn>
				<v-btn v-else color="primary" :disabled="!selectedCount" :loading="applying" @click="apply">Applica {{ selectedCount }} modifiche</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import KeyValueCard from '@/components/core/KeyValueCard.vue';

export default {
	name: 'CatalogImportDialog',
	components: { KeyValueCard },
	props: {
		value: { type: Boolean, default: false },
		// 'amazon' | 'ebay'
		platform: { type: String, required: true },
		fallbackMarketplaces: { type: Array, default: () => [] },
	},
	data() {
		return {
			step: 1,
			tab: 0,
			file: null,
			marketplace: null,
			importId: null,
			counters: {},
			// righe tolte dall'utente, per numero di riga: sopravvive al cambio pagina
			excluded: {},
			rows: [],
			total: 0,
			options: { page: 1, itemsPerPage: 25 },
			loadingMarketplaces: false,
			loadingPreview: false,
			loadingRows: false,
			applying: false,
			activeMarketplaces: [],
			headers: [
				{ text: '', value: 'include', sortable: false, width: 48 },
				{ text: 'Riga', value: 'line', width: 70 },
				{ text: 'SKU', value: 'sellerSku' },
				{ text: 'Titolo', value: 'title' },
				{ text: 'Prezzo', value: 'price', sortable: false },
				{ text: 'Giacenza', value: 'quantity', sortable: false },
				{ text: 'Stato', value: 'status', sortable: false },
			],
			invalidHeaders: [
				{ text: 'Riga', value: 'line', width: 70 },
				{ text: 'SKU', value: 'sellerSku' },
				{ text: 'Motivo dello scarto', value: 'error' },
			],
		};
	},
	computed: {
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		dialog: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit('input', val);
			},
		},
		excludedCount() {
			return Object.keys(this.excluded).length;
		},
		selectedCount() {
			return Math.max(0, (this.counters.changed || 0) - this.excludedCount);
		},
		pageIncluded() {
			return this.rows.length > 0 && this.rows.every(item => this.isIncluded(item));
		},
		pageIndeterminate() {
			return !this.pageIncluded && this.rows.some(item => this.isIncluded(item));
		},
		platformLabel() {
			return this.platform === 'ebay' ? 'eBay' : 'Amazon';
		},
		marketplaceOptions() {
			if (this.activeMarketplaces.length === 0) {
				return this.fallbackMarketplaces;
			}
			return this.activeMarketplaces.map(item => {
				const known = this.fallbackMarketplaces.find(option => option.value === item.marketplaceId);
				return { text: `${known ? known.text : item.marketplaceId} (${item.total})`, value: item.marketplaceId };
			});
		},
	},
	watch: {
		value(opened) {
			if (opened) {
				this.reset();
				this.loadMarketplaces();
			}
		},
		tab() {
			this.options.page = 1;
			this.fetchRows();
		},
		'options.page'() {
			this.fetchRows();
		},
		'options.itemsPerPage'() {
			this.options.page = 1;
			this.fetchRows();
		},
	},
	methods: {
		// coppie chiave/valore della card: per prezzo, giacenza e stato solo le variazioni (attuale → nuovo)
		campiCard(item) {
			const variazione = key => (item.next && item.next[key] !== undefined ? `${item.current[key]} → ${item.next[key]}` : '');
			return [
				{ label: 'Riga', value: item.line },
				{ label: 'Titolo', value: item.title },
				{ label: 'Prezzo', value: variazione('price'), strong: true },
				{ label: 'Giacenza', value: variazione('quantity'), strong: true },
				{ label: 'Stato', value: variazione('status'), strong: true },
			];
		},
		isIncluded(item) {
			return !this.excluded[item.line];
		},
		toggleRow(item) {
			if (this.excluded[item.line]) {
				this.$delete(this.excluded, item.line);
			} else {
				this.$set(this.excluded, item.line, true);
			}
		},
		// spunta di testata: vale solo per le righe della pagina visibile
		togglePage() {
			const include = !this.pageIncluded;
			this.rows.forEach(item => {
				if (include) {
					this.$delete(this.excluded, item.line);
				} else {
					this.$set(this.excluded, item.line, true);
				}
			});
		},
		includeAll() {
			this.excluded = {};
		},
		rowClass(item) {
			return this.isIncluded(item) ? '' : 'row-excluded';
		},
		reset() {
			this.step = 1;
			this.tab = 0;
			this.file = null;
			this.importId = null;
			this.counters = {};
			this.excluded = {};
			this.rows = [];
			this.total = 0;
			this.options = { page: 1, itemsPerPage: 25 };
		},
		async loadMarketplaces() {
			this.loadingMarketplaces = true;
			const res = await this.$store.dispatch('marketplaceCatalog/marketplaces', { platform: this.platform });
			this.loadingMarketplaces = false;
			this.activeMarketplaces = res && !res.error && Array.isArray(res.marketplaces) ? res.marketplaces : [];
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 6000 });
		},
		// carica il file e mostra il diff: a questo punto il backend non ha scritto nulla
		async loadPreview() {
			this.loadingPreview = true;
			const res = await this.$store.dispatch('marketplaceCatalog/preview', {
				platform: this.platform,
				marketplace: this.marketplace,
				file: this.file,
			});
			this.loadingPreview = false;

			if (!res || res.error) {
				this.notify('error', `Impossibile leggere il file: ${res && res.error !== true ? res.error : 'formato non valido'}`);
				return;
			}

			this.importId = res.importId;
			this.counters = res.counters || {};
			this.excluded = {};
			this.rows = (res.rows && res.rows.items) || [];
			this.total = (res.rows && res.rows.total) || 0;
			this.step = 2;

			if (!this.counters.changed) {
				this.notify('info', 'Nessuna modifica trovata nel file: le colonne Nuovo* risultano vuote o uguali ai valori attuali.');
			}
		},
		async fetchRows() {
			if (this.step !== 2 || !this.importId) {
				return;
			}
			this.loadingRows = true;
			const res = await this.$store.dispatch('marketplaceCatalog/rows', {
				platform: this.platform,
				importId: this.importId,
				page: this.options.page,
				limit: this.options.itemsPerPage,
				invalid: this.tab === 1,
			});
			this.loadingRows = false;

			if (res && !res.error) {
				this.rows = (res.rows && res.rows.items) || [];
				this.total = (res.rows && res.rows.total) || 0;
			}
		},
		async apply() {
			this.applying = true;
			const res = await this.$store.dispatch('marketplaceCatalog/apply', {
				platform: this.platform,
				importId: this.importId,
				excluded: Object.keys(this.excluded).map(Number),
			});
			this.applying = false;

			if (!res || res.error) {
				this.notify('error', "Errore durante l'avvio dell'aggiornamento. Riprova.");
				return;
			}

			this.notify('info', `Invio di ${this.selectedCount} modifiche al marketplace in corso: riceverai una mail e una notifica al termine.`);
			this.$emit('applied');
			this.close();
		},
		back() {
			this.step = 1;
		},
		close() {
			this.dialog = false;
		},
	},
};
</script>

<style scoped>
/* righe tolte dall'anteprima: restano visibili ma spente */
::v-deep .row-excluded {
	opacity: 0.45;
}
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.catalog-cards,
.catalog-cards tr {
	display: block;
	width: 100%;
}
.catalog-cards tr,
.catalog-cards tr:hover {
	background: transparent !important;
}
.catalog-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
/* respiro tra le card e il bordo della card che contiene la tabella */
.catalog-cards tr:first-child td {
	padding-top: 12px !important;
}
.catalog-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.catalog-cards tr + tr td {
	padding-top: 8px !important;
}
</style>
